import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type AuxiliaryTrendDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  seriesOptions: any[];
  selectedSeriesKey: string;
  onSeriesChange: (value: string) => void;
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (field: "startDate" | "endDate", value: string) => void;
  onApplyDateRange: () => void;
  onDownloadExcel: () => void;
  canDownload: boolean;
  isLoading: boolean;
  errorMessage: string;
};

const AuxiliaryTrendDialog = ({
  open,
  onClose,
  title,
  seriesOptions,
  selectedSeriesKey,
  onSeriesChange,
  dateRange,
  onDateRangeChange,
  onApplyDateRange,
  onDownloadExcel,
  canDownload,
  isLoading,
  errorMessage,
}: AuxiliaryTrendDialogProps) => {
  const selectedSeries = useMemo(
    () => seriesOptions.find((series) => series.key === selectedSeriesKey) || null,
    [seriesOptions, selectedSeriesKey]
  );

  const chartData = useMemo(
    () => ({
      labels: selectedSeries?.labels || [],
      datasets: [
        {
          label: selectedSeries?.unit ? `Value (${selectedSeries.unit})` : "Value",
          data: selectedSeries?.values || [],
          borderColor: "#1565c0",
          backgroundColor: "rgba(21, 101, 192, 0.25)",
          pointRadius: 0,
          borderWidth: 2,
          tension: 0,
        },
      ],
    }),
    [selectedSeries]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 0,
            minRotation: 0,
            maxTicksLimit: 8,
          },
          title: {
            display: true,
            text: "Time",
          },
        },
        y: {
          title: {
            display: true,
            text: selectedSeries?.unit ? `Value (${selectedSeries.unit})` : "Value",
          },
        },
      },
    }),
    [selectedSeries]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={dateRange?.startDate || ""}
            onChange={(event) => onDateRangeChange("startDate", event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={dateRange?.endDate || ""}
            onChange={(event) => onDateRangeChange("endDate", event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="outlined" onClick={onApplyDateRange} disabled={isLoading}>
            Apply Date Range
          </Button>
          <Button
            variant="contained"
            onClick={onDownloadExcel}
            disabled={isLoading || !canDownload}
          >
            Download Excel (CSV)
          </Button>
        </Box>

        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!isLoading && errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {!isLoading && !errorMessage && seriesOptions.length === 0 && (
          <Typography variant="body2">No historical data found for this parameter.</Typography>
        )}

        {!isLoading && !errorMessage && seriesOptions.length > 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Select
                  value={selectedSeriesKey || ""}
                  onChange={(event) => onSeriesChange(String(event.target.value))}
                >
                  {seriesOptions.map((series) => (
                    <MenuItem value={series.key} key={series.key}>
                      {series.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {selectedSeries?.title || "Selected Parameter"}
            </Typography>
            <Box sx={{ height: 420 }}>
              <Line data={chartData} options={options} />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuxiliaryTrendDialog;
